import { all, call, put, select, takeEvery } from 'redux-saga/effects'
import type { ApiError, CountryCode } from '@pbt/pbt-ui-components'

import * as API from '~/api'
import FeatureToggle from '~/constants/featureToggle'

import {
  type fetchGlobalItem,
  fetchGlobalItemFailure,
  fetchGlobalItemSuccess,
  type fetchGlobalList,
  fetchGlobalListFailure,
  fetchGlobalListSuccess,
  type fetchMoreGlobalList,
  fetchMoreGlobalListFailure,
  fetchMoreGlobalListSuccess,
  updateGlobalItems,
} from '../actions/globalInventoryCatalog'
import {
  FETCH_GLOBAL_ITEM,
  FETCH_GLOBAL_LIST,
  FETCH_MORE_GLOBAL_LIST,
} from '../actions/types/globaInventoryCatalog'
import { getCurrentBusinessCountryCatalogCode } from '../reducers/auth'
import {
  getCvcCumulusRhapsodyIntegrationInventoryEnabled,
  getFeatureToggle,
} from '../reducers/constants'
import requestAPI from './utils/requestAPI'

export function* fetchGlobalListSagaWorker(
  from: number,
  to: number,
  query: string,
  categories: string[],
) {
  const countryCode: CountryCode = yield select(
    getCurrentBusinessCountryCatalogCode,
  )
  const isFoodCatalogEnabled: boolean = yield select(
    getFeatureToggle(FeatureToggle.FOOD_CATALOG),
  )
  const isCvcCumulusRhapsodyIntegrationInventoryEnabled: boolean = yield select(
    getCvcCumulusRhapsodyIntegrationInventoryEnabled,
  )

  const {
    result: { data: list, totalCount },
    entities: { globalInventoryItems },
  } = yield* isCvcCumulusRhapsodyIntegrationInventoryEnabled
    ? requestAPI(
        API.fetchBusinessScopedGlobalInventoryCatalog,
        categories,
        query,
        from,
        to,
      )
    : requestAPI(
        API.fetchGlobalInventoryCatalog,
        categories,
        countryCode,
        query,
        from,
        to,
        isFoodCatalogEnabled,
      )

  yield put(updateGlobalItems(globalInventoryItems))

  return { list, totalCount }
}

export function* fetchGlobalListSaga({
  from,
  to,
  query,
  categories,
}: ReturnType<typeof fetchGlobalList>) {
  try {
    const { list, totalCount } = yield call(
      fetchGlobalListSagaWorker,
      from,
      to,
      query,
      categories,
    )

    yield put(fetchGlobalListSuccess(list, totalCount))
  } catch (error) {
    yield put(fetchGlobalListFailure(error as ApiError))
  }
}

export function* fetchMoreGlobalListSaga({
  categories,
  query,
  from,
  to,
}: ReturnType<typeof fetchMoreGlobalList>) {
  try {
    const { list, totalCount } = yield call(
      fetchGlobalListSagaWorker,
      from,
      to,
      query,
      categories,
    )

    yield put(fetchMoreGlobalListSuccess(list, totalCount, from))
  } catch (error) {
    yield put(fetchMoreGlobalListFailure(error as ApiError))
  }
}

export function* fetchGlobalItemSaga({
  id,
}: ReturnType<typeof fetchGlobalItem>) {
  try {
    const isFoodCatalogEnabled: boolean = yield select(
      getFeatureToggle(FeatureToggle.FOOD_CATALOG),
    )
    const isCvcCumulusRhapsodyIntegrationInventoryEnabled: boolean =
      yield select(getCvcCumulusRhapsodyIntegrationInventoryEnabled)

    const {
      entities: { globalInventoryItems },
    } = yield* requestAPI(
      isCvcCumulusRhapsodyIntegrationInventoryEnabled
        ? API.fetchBusinessScopedGlobalItem
        : isFoodCatalogEnabled
          ? API.fetchGlobalItemV2
          : API.fetchGlobalItem,
      id,
    )
    yield put(updateGlobalItems(globalInventoryItems))
    yield put(fetchGlobalItemSuccess())
  } catch (error) {
    yield put(fetchGlobalItemFailure(error as ApiError))
  }
}

function* watchFetchGlobalList() {
  yield takeEvery(FETCH_GLOBAL_LIST, fetchGlobalListSaga)
}

function* watchMoreFetchGlobalList() {
  yield takeEvery(FETCH_MORE_GLOBAL_LIST, fetchMoreGlobalListSaga)
}

function* watchFetchGlobalItem() {
  yield takeEvery(FETCH_GLOBAL_ITEM, fetchGlobalItemSaga)
}

export default function* globalInventoryCatalogSaga() {
  yield all([
    watchFetchGlobalList(),
    watchMoreFetchGlobalList(),
    watchFetchGlobalItem(),
  ])
}
